import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const FloatingNav = ({ onOpenModal, onOpenAdmin }) => {
  const { t, language, setLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 500);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };
  
  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Desktop Navigation */}
          <motion.nav 
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 hidden md:flex items-center gap-8 px-8 py-4 bg-black/90 backdrop-blur-2xl border border-white/5 rounded-full shadow-2xl"
            initial={{ opacity: 0, y: 60, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 60, scale: 0.9 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            data-testid="floating-nav"
          >
            <button 
              onClick={() => scrollToSection('philosophy')}
              className="text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-[#2bfecd] transition-colors duration-300"
              data-testid="nav-philosophy"
            >
              {t('nav.philosophy')}
            </button>
            
            <button 
              onClick={() => scrollToSection('experience')}
              className="text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-[#2bfecd] transition-colors duration-300"
              data-testid="nav-experience"
            >
              {t('nav.experience')}
            </button>
            
            <button 
              onClick={() => scrollToSection('gallery')}
              className="text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-[#2bfecd] transition-colors duration-300"
              data-testid="nav-gallery"
            >
              {t('nav.gallery')}
            </button>
            
            <button 
              onClick={onOpenModal}
              className="text-[10px] tracking-[0.2em] uppercase text-[#2bfecd] hover:text-white transition-colors duration-300"
              style={{ textShadow: '0 0 20px rgba(43, 254, 205, 0.5)' }}
              data-testid="nav-membership"
            >
              {t('nav.membership')}
            </button>
            
            {/* Language Toggle */}
            <div className="flex items-center gap-2 pl-6 border-l border-white/10">
              <button 
                onClick={() => setLanguage('en')}
                className={`text-[9px] tracking-[0.15em] uppercase transition-colors duration-300 ${language === 'en' ? 'text-[#2bfecd]' : 'text-white/30 hover:text-white/60'}`}
                data-testid="lang-en"
              >
                EN
              </button>
              <span className="text-white/20">|</span>
              <button 
                onClick={() => setLanguage('de')}
                className={`text-[9px] tracking-[0.15em] uppercase transition-colors duration-300 ${language === 'de' ? 'text-[#2bfecd]' : 'text-white/30 hover:text-white/60'}`}
                data-testid="lang-de"
              >
                DE
              </button>
            </div>
            
            {/* Admin Button (hidden, accessible via keyboard shortcut) */}
            <button 
              onClick={onOpenAdmin}
              className="absolute -top-20 left-1/2 -translate-x-1/2 text-[8px] tracking-[0.2em] uppercase text-white/10 hover:text-[#2bfecd] transition-colors px-4 py-2 border border-white/5 hover:border-[#2bfecd]/30 rounded-full opacity-0 hover:opacity-100"
              data-testid="nav-admin"
            >
              Admin
            </button>
          </motion.nav>
          
          {/* Mobile Navigation */}
          <motion.div 
            className="fixed bottom-20 right-6 z-40 md:hidden"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="w-14 h-14 bg-black/90 backdrop-blur-xl border border-white/10 rounded-full flex items-center justify-center shadow-2xl"
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5 text-[#2bfecd]" />
              ) : (
                <Menu className="w-5 h-5 text-[#2bfecd]" />
              )}
            </button>
            
            {/* Mobile Menu Popup */}
            <AnimatePresence>
              {isMobileMenuOpen && (
                <motion.div
                  className="absolute bottom-16 right-0 w-48 bg-black/95 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="p-4 space-y-1">
                    <button 
                      onClick={() => scrollToSection('philosophy')}
                      className="w-full text-left px-4 py-3 text-xs tracking-[0.15em] uppercase text-white/60 hover:text-[#2bfecd] hover:bg-white/5 rounded-lg transition-colors"
                    >
                      {t('nav.philosophy')}
                    </button>
                    
                    <button 
                      onClick={() => scrollToSection('experience')}
                      className="w-full text-left px-4 py-3 text-xs tracking-[0.15em] uppercase text-white/60 hover:text-[#2bfecd] hover:bg-white/5 rounded-lg transition-colors"
                    >
                      {t('nav.experience')}
                    </button>
                    
                    <button 
                      onClick={() => scrollToSection('gallery')}
                      className="w-full text-left px-4 py-3 text-xs tracking-[0.15em] uppercase text-white/60 hover:text-[#2bfecd] hover:bg-white/5 rounded-lg transition-colors"
                    >
                      {t('nav.gallery')}
                    </button>
                    
                    <button 
                      onClick={() => { onOpenModal(); setIsMobileMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 text-xs tracking-[0.15em] uppercase text-[#2bfecd] hover:bg-[#2bfecd]/10 rounded-lg transition-colors"
                    >
                      {t('nav.membership')}
                    </button>
                    
                    {/* Language Toggle */}
                    <div className="flex items-center gap-3 px-4 py-3 border-t border-white/10 mt-2">
                      <button 
                        onClick={() => setLanguage('en')}
                        className={`text-xs tracking-[0.1em] uppercase transition-colors ${language === 'en' ? 'text-[#2bfecd]' : 'text-white/40'}`}
                      >
                        EN
                      </button>
                      <span className="text-white/20">|</span>
                      <button 
                        onClick={() => setLanguage('de')}
                        className={`text-xs tracking-[0.1em] uppercase transition-colors ${language === 'de' ? 'text-[#2bfecd]' : 'text-white/40'}`}
                      >
                        DE
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FloatingNav;
